import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuth, reload } from 'firebase/auth';
import { Mail, RefreshCw } from 'lucide-react';
import { User } from '../types';

interface AuthGuardProps {
  user: User | null;
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ user, children }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // 1. If there is no user at all, kick them to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 2. THE TRAP: If logged in but NOT verified, block access to children
  if (!user.emailVerified) {
    const handleCheckVerification = async () => {
      setIsChecking(true);
      setErrorMsg('');
      
      try {
        const auth = getAuth();
        if (auth.currentUser) {
          // Force Firebase to fetch the absolute latest status from the server
          await reload(auth.currentUser);
          
          if (auth.currentUser.emailVerified) {
            // SUCCESS: Force a hard page reload to refresh the global App.tsx state 
            // and let them into the dashboard
            window.location.reload();
          } else {
            // FAIL: They clicked it without actually verifying
            setErrorMsg("Your email is still not verified. Please check your inbox and spam folder.");
          }
        }
      } catch (error) {
        console.error(error);
        setErrorMsg("An error occurred connecting to the server. Please try logging out and back in.");
      }
      
      setIsChecking(false);
    };

    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
        <div className="bg-white/5 border border-white/10 p-8 rounded-2xl max-w-md w-full text-center shadow-2xl">
          <Mail className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h2 className="text-2xl font-serif text-white mb-2">Verify Your Email</h2>
          <p className="text-white/60 text-sm mb-8">
            We sent a verification link to <span className="font-bold text-white">{user.email}</span>. 
            You must click the secure link in that email before you can access the dashboard.
          </p>
          
          {errorMsg && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs p-3 rounded mb-6 text-left">
              {errorMsg}
            </div>
          )}

          <button 
            onClick={handleCheckVerification}
            disabled={isChecking}
            className="w-full py-3 bg-white text-black hover:bg-gray-200 rounded-full text-sm font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isChecking ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
            {isChecking ? "Verifying with server..." : "I have verified my email"}
          </button>
        </div>
      </div>
    );
  }

  // 3. If they are logged in AND verified, render the protected route normally
  return <>{children}</>;
};

export default AuthGuard;
